/**
 * MachineDetailPanel — Complete machine cloud panel with 4 tabs
 * Tabs: Visão Geral | Acesso SSH | Timeline | Configurações
 */

import { useState, useEffect, useCallback } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../api/client';
import { ConfirmDialog } from './ui';
import { SetupModal } from './SetupModal';
import { showToast } from './Toast';
import { SkeletonBar } from './ui';
import { T } from '../styles/tokens';
import { Button } from './ui';

// ══════════════════════════════════════════════════════════════════════════
// THEME
// ══════════════════════════════════════════════════════════════════════════

const _T = {
  bg: T.bg,
  card: T.bgCard,
  cardHover: '#161f33',
  primary: T.primary,
  cyan: '#06b6d4',
  purple: T.purple,
  indigo: T.accent,
  text: T.text,
  muted: T.textSecondary,
  dimmed: T.textMuted,
  success: T.success,
  warning: T.warning,
  error: T.danger,
  border: T.border,
  borderSolid: T.bgSecondary,
  gradient: 'linear-gradient(135deg, #06b6d4 0%, #8B5CF6 100%)',
  gradientSubtle: `linear-gradient(135deg, rgba(6,182,212,0.10) 0%, ${T.purpleBg} 100%)`,
  font: "'Manrope', system-ui, sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', monospace",
};

const STATUS_MAP: Record<string, { label: string; color: string; glow: boolean }> = {
  running: { label: 'Online', color: T.success, glow: true },
  provisioning: { label: 'Criando...', color: T.warning, glow: true },
  configuring_setup: { label: 'Configurando...', color: T.warning, glow: true },
  installing_openclaw: { label: 'Instalando...', color: T.purple, glow: true },
  installing_llm: { label: 'Configurando IA...', color: T.primary, glow: true },
  stopped: { label: 'Offline', color: _T.error, glow: false },
  error: { label: 'Erro', color: _T.error, glow: false },
  timeout_provisioning: { label: 'Aguardando...', color: T.warning, glow: true },
};

// ══════════════════════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════════════════════

interface MachineDetails {
  machine: {
    id: string; name: string; status: string; provider: string;
    ipv4_address: string | null; provider_location: string | null;
    namespace?: string | null; pod_name?: string | null; created_at: string;
    provisioning_completed_at: string | null; error_message: string | null;
  };
  plan: { name: string; cpus: number; memory_gb: number; storage_gb: number; bandwidth_gb: number; price_usd: number } | null;
  order: { id: string; status: string; created_at: string } | null;
  services: {
    elon_tools: { installed: boolean };
    openclaw: { active: boolean; response_ms?: number };
    runner_v3: { connected: boolean; runner_id: string | null; status: string | null };
    llm_router: { active: boolean; token_id: string | null };
  };
  uptime_ms: number;
}

interface MachineEvent {
  id: string; machine_id: string; event_type: string; event_data: string; created_at: string;
}

interface SSHCreds {
  username: string | null; password: string | null; private_key: string | null; has_credentials: boolean;
  // K8s: kept for backward compat, SSH tab replaced by Pod Info
  capture_status?: 'pending' | 'success' | 'failed' | 'manual_review' | null;
  last_reset?: string | null;
  encrypted_at?: string | null;
}

type Tab = 'overview' | 'pod' | 'timeline' | 'settings' | 'brain';

// ══════════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════════

function formatUptime(ms: number): string {
  if (ms <= 0) return '—';
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min atrás`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  return `${days}d atrás`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function copyText(text: string, setCopied: (k: string) => void, key: string) {
  navigator.clipboard.writeText(text).catch(() => {
    const ta = document.createElement('textarea');
    ta.value = text; document.body.appendChild(ta); ta.select();
    document.execCommand('copy'); document.body.removeChild(ta);
  });
  setCopied(key);
  setTimeout(() => setCopied(''), 2000);
}

const EVENT_ICONS: Record<string, string> = {
 provisioning_started: '', provisioning_progress: '', setup_complete: '',
 status_running: '', status_error: '', reconciled: '',
 install_elon_tools: '', force_install_initiated: '',
 ssh_password_reset: '', ssh_password_captured: '',
 default: '',
};

// ══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════════════════

export function MachineDetailPanel({ machineId }: { machineId: string }) {
  const [details, setDetails] = useState<MachineDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('overview');
  const [copied, setCopied] = useState('');

  // SSH tab state
  const [sshCreds, setSSHCreds] = useState<SSHCreds | null>(null);
  const [sshLoading, setSSHLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  // Timeline tab state
  const [events, setEvents] = useState<MachineEvent[]>([]);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Brain tab state
  const [brainData, setBrainData] = useState<any>(null);
  const [brainLoading, setBrainLoading] = useState(false);

  // Settings state
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    title: string; message: string; confirmText: string;
    variant: 'destructive' | 'normal';
    action: string; endpoint: string; method?: 'POST' | 'DELETE';
    redirectTo?: string;
  } | null>(null);
  const [showReinstallModal, setShowReinstallModal] = useState(false);

  // ── Fetch details ──
  const fetchDetails = useCallback(async () => {
    try {
      const res = await api.get<MachineDetails>(`/cloud/machines/${machineId}/details`);
      setDetails(res);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar detalhes');
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  // Auto-refresh every 30s if running
  useEffect(() => {
    if (details?.machine.status !== 'running') return;
    const interval = setInterval(fetchDetails, 30000);
    return () => clearInterval(interval);
  }, [details?.machine.status, fetchDetails]);

  // ── Fetch SSH creds ──
  const fetchSSH = useCallback(async () => {
    setSSHLoading(true);
    try {
      const res = await api.get<any>(`/cloud/machines/${machineId}/ssh`);
      setSSHCreds(res?.ssh || null);
    } catch { /* ignore */ }
    setSSHLoading(false);
  }, [machineId]);

  useEffect(() => {
    if (tab === 'ssh') fetchSSH();
  }, [tab, fetchSSH]);



  // ── Fetch events ──
  const fetchEvents = useCallback(async (page: number) => {
    setEventsLoading(true);
    try {
      const res = await api.get<any>(`/cloud/machines/${machineId}/events?page=${page}&limit=20`);
      setEvents(res?.events || []);
      setEventsTotalPages(res?.pagination?.total_pages || 1);
    } catch { /* ignore */ }
    setEventsLoading(false);
  }, [machineId]);

  useEffect(() => {
    if (tab === 'timeline') fetchEvents(eventsPage);
  }, [tab, eventsPage, fetchEvents]);

  const fetchBrain = useCallback(async () => {
    setBrainLoading(true);
    try {
      const res = await api.get(`/cloud/machines/${machineId}/brain`);
      setBrainData(res.data?.data || res.data || null);
    } catch { setBrainData(null); }
    finally { setBrainLoading(false); }
  }, [machineId]);

  useEffect(() => {
    if (tab === 'brain') fetchBrain();
  }, [tab, fetchBrain]);

  // ── Actions ──
  async function handleCreateInstallationRequest() {
    setActionLoading('Instalar WhiteLabelClaw');
    setActionResult(null);
    try {
      // Create installation request (goes to queue for admin approval)
      const res = await api.post<{ id: string; status: string; created_at: string; message: string }>(`/cloud/installation-requests`, {
        machine_id: machineId,
        llm_provider: 'elon',
        llm_model: 'auto',
        brain: 'clean',
        chat_language: 'pt',
      });
      
      const requestId = res?.id;
      if (!requestId) {
        showToast('Pedido criado, mas ID não retornou. Redirecionando...', 'error');
        setActionLoading(null);
        setTimeout(() => route(`/cloud/machines/${machineId}`), 1000);
        return;
      }
      
 showToast('Pedido de instalação criado! Redirecionando...', 'success');
      setActionLoading(null);
      // Redirect to confirmation screen
      route(`/cloud/machines/${machineId}/installation-request/${requestId}`);
    } catch (e: any) {
      const msg = e?.message || 'Erro ao criar pedido de instalação';
      showToast(`Erro: ${msg}`, 'error');
      setActionResult({ type: 'error', msg });
      setActionLoading(null);
    }
  }

  async function handleAction(action: string, endpoint: string, method: 'POST' | 'GET' = 'POST', redirectTo?: string) {
    setActionLoading(action);
    setActionResult(null);
    try {
      if (method === 'DELETE' as any) {
        await api.delete(`/api/v1/machines/${machineId}`);
      } else if (method === 'POST') {
        await api.post(`/cloud/machines/${machineId}/${endpoint}`);
      } else {
        await api.get(`/cloud/machines/${machineId}/${endpoint}`);
      }
 showToast(`${action} executado com sucesso! `, 'success');
      setActionResult({ type: 'success', msg: `${action} executado com sucesso!` });
      if (redirectTo) {
        setTimeout(() => route(redirectTo), 1500);
      } else {
        setTimeout(fetchDetails, 2000);
      }
    } catch (e: any) {
      const msg = e?.message || `Erro ao executar ${action}`;
      showToast(`Erro: ${msg}`, 'error');
      setActionResult({ type: 'error', msg });
    }
    setActionLoading(null);
  }

  async function handleConfirmedAction() {
    if (!confirmModal) return;
    const { action, endpoint, method, redirectTo } = confirmModal;
    await handleAction(action, endpoint, (method || 'POST') as any, redirectTo);
    setConfirmModal(null);
  }

  function openConfirmModal(config: typeof confirmModal) {
    setConfirmModal(config);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div style={{
        padding: 40, textAlign: 'center', borderRadius: 20,
        background: _T.gradientSubtle, border: `1px solid ${_T.borderSolid}`,
        marginBottom: 24,
      }}>
 <div style={{ fontSize: 32, marginBottom: 12, animation: 'spin 1s linear infinite' }}></div>
        <div style={{ padding: 16 }}><SkeletonBar width="60%" height={16} style={{ marginBottom: 12 }} /><SkeletonBar width="80%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={14} /></div>
      </div>
    );
  }

  if (error || !details) {
    return (
      <div style={{
        padding: 24, borderRadius: 20, marginBottom: 24,
        background: T.dangerBg, border: `1px solid ${_T.dangerBg}`,
        textAlign: 'center',
      }}>
 <div style={{ fontSize: 14, color: _T.error }}> {error || 'Erro ao carregar'}</div>
        <Button variant="ghost" onClick={fetchDetails} style={{
          marginTop: 12, padding: '8px 18px', borderRadius: 8, border: 'none',
          background: _T.gradient, color: T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
 }}>Tentar novamente</Button>
      </div>
    );
  }

  const m = details.machine;
  const st = STATUS_MAP[m.status] || { label: m.status, color: _T.dimmed, glow: false };
  const ip = m.ipv4_address || '—';
  const svc = details.services;

  const TABS: { id: Tab; label: string; icon: string }[] = [
 { id: 'overview', label: 'Visão Geral', icon: '' },
 { id: 'pod', label: 'Pod Info', icon: '' },
 { id: 'timeline', label: 'Timeline', icon: '' },
 { id: 'settings', label: 'Configurações', icon: '' },
 { id: 'brain', label: 'Cérebro', icon: '' },
  ];

  return (
    <div style={{
      borderRadius: 12, overflow: 'hidden', marginBottom: 12,
      background: _T.card, border: `1px solid ${_T.borderSolid}`,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
    }}>
      {/* ═══ HEADER (MESMO VISUAL DOS OUTROS CARDS) ═══ */}
      <div style={{
        padding: '16px', background: T.bg, display: 'flex', alignItems: 'flex-start', gap: 16,
        borderBottom: `1px solid ${_T.borderSolid}`,
      }}>
        {/* Ícone grande à esquerda */}
        <div style={{
          width: 56, height: 56, borderRadius: 16, background: _T.gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26, boxShadow: `0 4px 20px rgba(6,182,212,0.3)`,
          flexShrink: 0,
        }}>
 
        </div>

        {/* Info à direita do ícone */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Linha 1: Nome + Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {m.name}
            </h3>
            <span style={{
              padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
              color: st.color, background: `${st.color}18`,
              display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%', background: st.color,
                boxShadow: st.glow ? `0 0 8px ${st.color}` : 'none',
                display: 'inline-block',
              }} />
              {st.label}
            </span>
          </div>

          {/* Badges inline (Linux, IP, Uptime, etc) */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11, color: _T.muted, marginBottom: 8 }}>
 <span style={{ background: T.bgCardHover, padding: '2px 8px', borderRadius: 4 }}>Linux x64</span>
 {m.provider_location && <span style={{ background: T.bgCardHover, padding: '2px 8px', borderRadius: 4 }}> {m.provider_location}</span>}
            {m.ipv4_address && (
              <span style={{ background: _T.primaryBg, padding: '2px 8px', borderRadius: 4, color: _T.cyan, cursor: 'pointer', fontFamily: _T.mono, fontSize: 10 }}
                onClick={() => copyText(m.ipv4_address!, setCopied, 'header-ip')}>
                {copied === 'header-ip' ? 'Copiado!' : m.ipv4_address}
              </span>
            )}
            <span style={{ background: T.bgCardHover, padding: '2px 8px', borderRadius: 4, marginLeft: 'auto' }}>
 {formatUptime(details.uptime_ms)}
            </span>
          </div>

          {/* Service status badges */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
 { label: 'WhiteLabelClaw', active: svc.elon_tools.installed, icon: '' },
 { label: 'OpenClaw', active: svc.openclaw.active, icon: '' },
 { label: 'Runner V3', active: svc.runner_v3.connected, icon: '' },
 { label: 'LLM Router', active: svc.llm_router.active, icon: '' },
            ].map(b => (
              <div key={b.label} style={{
                padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: b.active ? T.successBg : 'rgba(107,122,144,0.08)',
                border: `1px solid ${b.active ? T.successBg : 'rgba(107,122,144,0.12)'}`,
                color: b.active ? T.success : _T.dimmed,
              }}>
                <span>{b.icon}</span>
                <span style={{ display: { xs: 'none', sm: 'inline' } }}>{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ TABS (COMPACTADO) ═══ */}
      <div style={{
        display: 'flex', borderBottom: `1px solid ${_T.borderSolid}`,
        overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        background: 'rgba(0,0,0,0.1)',
      }}>
        {TABS.map(t => (
          <Button variant="ghost" key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 12px', background: 'none', border: 'none',
            borderBottom: tab === t.id ? `2px solid ${_T.cyan}` : '2px solid transparent',
            color: tab === t.id ? _T.cyan : _T.dimmed,
            fontSize: 11, fontWeight: 600, cursor: 'pointer',
            whiteSpace: 'nowrap', transition: 'all 0.2s', fontFamily: _T.font,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <span style={{ fontSize: 12 }}>{t.icon}</span>
            <span style={{ display: { xs: 'none', sm: 'inline' } }}>{t.label}</span>
          </Button>
        ))}
      </div>

      {/* ═══ TAB CONTENT ═══ */}
      <div style={{ padding: '16px 14px' }}>

        {/* ─── TAB 1: VISÃO GERAL (COMPACTA) ─── */}
        {tab === 'overview' && (
          <div>
            {/* Mini stats (1 linha) */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
              <div>
                <div style={{ fontSize: 10, color: _T.dimmed, fontWeight: 700, textTransform: 'uppercase' }}>Status</div>
                <div style={{ color: st.color, fontWeight: 700, marginTop: 2 }}>{st.label}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: _T.dimmed, fontWeight: 700, textTransform: 'uppercase' }}>IP</div>
                <div style={{ color: _T.cyan, fontFamily: _T.mono, marginTop: 2, cursor: 'pointer' }} onClick={() => copyText(ip, setCopied, 'overview-ip')}>
                  {copied === 'overview-ip' ? '' : ip}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: _T.dimmed, fontWeight: 700, textTransform: 'uppercase' }}>Plano</div>
                <div style={{ color: T.purple, marginTop: 2 }}>{details.plan?.name || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: _T.dimmed, fontWeight: 700, textTransform: 'uppercase' }}>CPU</div>
                <div style={{ color: T.primary, marginTop: 2 }}>{details.plan ? `${details.plan.cpus} vCPU` : '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: _T.dimmed, fontWeight: 700, textTransform: 'uppercase' }}>RAM</div>
                <div style={{ color: T.primary, marginTop: 2 }}>{details.plan ? `${details.plan.memory_gb} GB` : '—'}</div>
              </div>
            </div>

            {/* Quick actions */}
            {m.status === 'running' && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
 <QuickBtn label="Terminal K8s" icon="" onClick={() => { window.location.href = `/k8s-terminal/${machineId}`; }}
                  active={false} activeLabel="Terminal" />
 <QuickBtn label="Copiar IP" icon="" onClick={() => copyText(ip, setCopied, 'quick-ip')}
                  active={copied === 'quick-ip'} activeLabel="Copiado! " />
              </div>
            )}

            {/* Action result toast */}
            {actionResult && (
              <div style={{
                marginTop: 8, padding: 10, borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: actionResult.type === 'success' ? T.successBg : T.dangerBg,
                border: `1px solid ${actionResult.type === 'success' ? T.successBg : T.dangerBg}`,
                color: actionResult.type === 'success' ? T.success : _T.error,
              }}>
 {actionResult.type === 'success' ? '' : ''} {actionResult.msg}
              </div>
            )}
          </div>
        )}

        {/* ─── TAB 2: POD INFO (K8s) ─── */}
        {tab === 'pod' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Pod details */}
            <div>
              <div style={{ fontSize: 11, color: _T.dimmed, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Informações do Pod
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'Namespace', value: m.namespace || m.pod_name ? `ws-${machineId.slice(0, 8)}` : '—' },
                  { label: 'Pod', value: m.pod_name || `workspace-${machineId.slice(0, 8)}` },
                  { label: 'Status', value: m.status },
                  { label: 'Criado em', value: m.created_at ? new Date(m.created_at).toLocaleString('pt-BR') : '—' },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', background: _T.bg, borderRadius: 8, border: `1px solid ${_T.borderSolid}` }}>
                    <span style={{ fontSize: 13, color: _T.dimmed }}>{item.label}</span>
                    <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Button variant="ghost" onClick={() => { window.location.href = `/k8s-terminal/${machineId}`; }} style={{
                padding: '10px 20px', borderRadius: 10, border: 'none',
                background: _T.gradient, color: T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>Abrir Terminal</Button>
              <Button variant="ghost" onClick={() => { api.post(`/cloud/workspaces/${machineId}/restart`).then(() => window.location.reload()); }} style={{
                padding: '10px 20px', borderRadius: 10, border: `1px solid ${_T.borderSolid}`,
                background: T.bgCardHover, color: T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>Reiniciar Pod</Button>
            </div>

            {/* Tip */}
            <div style={{
              padding: 14, borderRadius: 10,
              background: 'rgba(50,108,229,0.06)', border: '1px solid rgba(50,108,229,0.12)',
              fontSize: 12, color: _T.muted, lineHeight: 1.6,
            }}>
              <strong style={{ color: T.text }}>Kubernetes:</strong> Seu workspace roda em um pod isolado com namespace dedicado. Use o terminal para acessar via kubectl exec.
            </div>
          </div>
        )}

        {/* ─── TAB 3: TIMELINE ─── */}
        {tab === 'timeline' && (
          <div>
            {eventsLoading ? (
 <div style={{ padding: 16 }}>{[1,2,3].map(i => (<div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}><SkeletonBar width="20%" height={14} /><SkeletonBar width="40%" height={14} /><SkeletonBar width="25%" height={14} /></div>))}</div>
            ) : events.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: _T.dimmed }}>
 <div style={{ fontSize: 36, marginBottom: 12 }}></div>
                Nenhum evento registrado.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
                  {/* Timeline line */}
                  <div style={{
                    position: 'absolute', left: 15, top: 0, bottom: 0, width: 2,
                    background: _T.borderSolid, zIndex: 0,
                  }} />
                  {events.map(ev => {
                    const icon = EVENT_ICONS[ev.event_type] || EVENT_ICONS.default;
                    let detail = '';
                    try {
                      const d = JSON.parse(ev.event_data);
                      detail = d.message || d.status || ev.event_type;
                    } catch {
                      detail = ev.event_data || ev.event_type;
                    }
                    return (
                      <div key={ev.id} style={{
                        display: 'flex', gap: 14, padding: '10px 0',
                        position: 'relative', zIndex: 1,
                      }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: T.bg, border: `2px solid ${_T.borderSolid}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, flexShrink: 0,
                        }}>
                          {icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{ev.event_type.replace(/_/g, ' ')}</div>
                          <div style={{ fontSize: 12, color: _T.muted, marginTop: 2, wordBreak: 'break-word' }}>{detail}</div>
                          <div style={{ fontSize: 11, color: _T.dimmed, marginTop: 4 }}>{formatDate(ev.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {eventsTotalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
                    <PageBtn label="← Anterior" disabled={eventsPage <= 1} onClick={() => setEventsPage(p => p - 1)} />
                    <span style={{ fontSize: 13, color: _T.muted, padding: '8px 12px' }}>
                      {eventsPage} / {eventsTotalPages}
                    </span>
                    <PageBtn label="Próxima →" disabled={eventsPage >= eventsTotalPages} onClick={() => setEventsPage(p => p + 1)} />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─── TAB 4: CONFIGURAÇÕES ─── */}
        {tab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Service actions */}
 <SettingsSection title="Serviços">
 <SettingsBtn label="Reinstalar WhiteLabelClaw" icon="" desc="Reinstala OpenClaw + WhiteLabelClaw + Runner V3 (com configurações)"
                onClick={() => setShowReinstallModal(true)}
                loading={actionLoading === 'Reinstalar WhiteLabelClaw'} color={T.primary} />
 <SettingsBtn label="Instalar WhiteLabelClaw" icon="" desc="Cria pedido na fila para aprovação do admin"
                onClick={() => handleCreateInstallationRequest()}
                loading={actionLoading === 'Instalar WhiteLabelClaw'} color={T.success} />
            </SettingsSection>

            {/* Danger zone */}
 <SettingsSection title="Zona de Perigo">
 <SettingsBtn label="Reinstalar Máquina" icon="" desc="Reinstala o sistema operacional do zero"
                onClick={() => openConfirmModal({
                  title: 'Reinstalar Máquina?',
 message: 'AÇÃO IRREVERSÍVEL! Todos os dados serão apagados e o sistema reinstalado do zero.',
                  confirmText: 'Reinstalar',
                  variant: 'destructive',
                  action: 'Reinstalar Máquina',
                  endpoint: 'rebuild',
                  redirectTo: `/cloud/machines/${machineId}/install`,
                })}
                loading={actionLoading === 'Reinstalar Máquina'} color={_T.error} />
              <div style={{
                padding: 16, borderRadius: 12,
                background: T.dangerBg, border: `1px solid ${_T.dangerBg}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: _T.error, display: 'flex', alignItems: 'center', gap: 8 }}>
 Deletar Máquina
                    </div>
                    <div style={{ fontSize: 12, color: _T.muted, marginTop: 4 }}>Esta ação é irreversível. Todos os dados serão perdidos.</div>
                  </div>
                  <Button variant="ghost"
                    onClick={() => openConfirmModal({
                      title: `Deletar ${m.name}?`,
 message: 'AÇÃO IRREVERSÍVEL! A máquina será destruída permanentemente. Todos os dados serão perdidos.',
 confirmText: 'Deletar',
                      variant: 'destructive',
                      action: 'Deletar Máquina',
                      endpoint: '',
                      method: 'DELETE',
                      redirectTo: '/machines',
                    })}
                    style={{
                      padding: '8px 18px', borderRadius: 8, border: `1px solid ${_T.dangerBgStrong}`,
                      background: T.dangerBg, color: _T.error,
                      fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: _T.font,
                    }}
                  >
                    Deletar
                  </Button>
                </div>
              </div>
            </SettingsSection>

            {/* Action result */}
            {actionResult && (
              <div style={{
                padding: 12, borderRadius: 10, fontSize: 13, fontWeight: 600,
                background: actionResult.type === 'success' ? T.successBg : T.dangerBg,
                border: `1px solid ${actionResult.type === 'success' ? T.successBg : T.dangerBg}`,
                color: actionResult.type === 'success' ? T.success : _T.error,
              }}>
 {actionResult.type === 'success' ? '' : ''} {actionResult.msg}
              </div>
            )}
          </div>
        )}
        {/* ─── TAB 5: CÉREBRO ─── */}
        {tab === 'brain' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {brainLoading ? (
              <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
            ) : !brainData ? (
              <div style={{ textAlign: 'center', padding: 40, color: _T.dimmed }}>
 <div style={{ fontSize: 48, marginBottom: 12 }}></div>
                <div>Nenhum snapshot do cérebro encontrado</div>
                <div style={{ fontSize: 12, marginTop: 8 }}>O auto-sync do runner envia snapshots automaticamente a cada hora.</div>
              </div>
            ) : (
              <>
                {/* Meta info */}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {brainData.openclaw_version && (
                    <div style={{ padding: '8px 14px', borderRadius: 8, background: T.accentBg, border: `1px solid ${_T.accentBg}` }}>
                      <div style={{ fontSize: 10, color: _T.dimmed }}>OpenClaw Version</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{brainData.openclaw_version}</div>
                    </div>
                  )}
                  {brainData.last_auto_sync && (
                    <div style={{ padding: '8px 14px', borderRadius: 8, background: T.successBg, border: `1px solid ${_T.successBg}` }}>
                      <div style={{ fontSize: 10, color: _T.dimmed }}>Último Sync</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{new Date(brainData.last_auto_sync).toLocaleString('pt-BR')}</div>
                    </div>
                  )}
                  {brainData.snapshot_at && (
                    <div style={{ padding: '8px 14px', borderRadius: 8, background: T.primaryBg, border: `1px solid ${_T.primaryBg}` }}>
                      <div style={{ fontSize: 10, color: _T.dimmed }}>Snapshot</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{new Date(brainData.snapshot_at).toLocaleString('pt-BR')}</div>
                    </div>
                  )}
                </div>

                {/* Brain files */}
                {[
 { key: 'soul_md', label: 'SOUL.md', icon: '', desc: 'Identidade do agente' },
 { key: 'user_md', label: 'USER.md', icon: '', desc: 'Perfil do dono' },
 { key: 'memory_md', label: 'MEMORY.md', icon: '', desc: 'Memória de longo prazo' },
 { key: 'tools_md', label: 'TOOLS.md', icon: '', desc: 'Integrações documentadas' },
 { key: 'agents_md', label: 'AGENTS.md', icon: '', desc: 'Configurações do agente' },
                ].map(f => brainData[f.key] ? (
                  <div key={f.key} style={{ borderRadius: 8, border: `1px solid ${_T.borderSolid}`, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', background: T.bgSubtle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{f.icon} {f.label} <span style={{ fontWeight: 400, color: _T.dimmed, fontSize: 11 }}>— {f.desc}</span></span>
                      <span style={{ fontSize: 10, color: _T.dimmed }}>{(brainData[f.key]?.length || 0).toLocaleString()} chars</span>
                    </div>
                    <pre style={{
                      padding: '12px 14px', margin: 0, fontSize: 11, lineHeight: 1.5,
                      background: '#0a0a12', color: '#c5c5d2', overflow: 'auto', maxHeight: 300,
                      fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>{brainData[f.key]}</pre>
                  </div>
                ) : null)}
              </>
            )}
          </div>
        )}
      </div>

      {/* ═══ CONFIRM MODAL ═══ */}
      <ConfirmDialog
        open={!!confirmModal}
        title={confirmModal?.title ?? 'Confirmar'}
        message={confirmModal?.message ?? ''}
        confirmText={confirmModal?.confirmText}
        danger={confirmModal?.variant === 'destructive'}
        loading={!!actionLoading}
        onConfirm={handleConfirmedAction}
        onClose={() => setConfirmModal(null)}
      />

      {/* ═══ REINSTALL MODAL ═══ */}
      {showReinstallModal && (
        <SetupModal
          mode="reinstall"
          machineId={machineId}
          machineName={m?.name || m?.display_name || ''}
          onClose={() => setShowReinstallModal(false)}
          onSuccess={() => {
            setTimeout(fetchDetails, 2000);
            route(`/machines`);
          }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ══════════════════════════════════════════════════════════════════════════

const iconBtnStyle: any = {
  background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 4,
};

function CopyBtn({ text, copiedKey, copied, setCopied }: {
  text: string; copiedKey: string; copied: string; setCopied: (k: string) => void;
}) {
  return (
    <Button variant="ghost" onClick={() => copyText(text, setCopied, copiedKey)} style={iconBtnStyle}>
 {copied === copiedKey ? '' : ''}
    </Button>
  );
}

function CredField({ label, value, copiedKey, copied, setCopied }: {
  label: string; value: string; copiedKey: string; copied: string; setCopied: (k: string) => void;
}) {
  return (
    <div style={{
      background: _T.bg, borderRadius: 10, padding: '12px 16px',
      border: `1px solid ${_T.borderSolid}`,
    }}>
      <div style={{ fontSize: 10, color: _T.dimmed, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <code style={{ fontSize: 14, color: T.success, fontFamily: _T.mono }}>{value}</code>
        <CopyBtn text={value} copiedKey={copiedKey} copied={copied} setCopied={setCopied} />
      </div>
    </div>
  );
}

function QuickBtn({ label, icon, onClick, active, activeLabel, loading }: {
  label: string; icon: string; onClick: () => void;
  active?: boolean; activeLabel?: string; loading?: boolean;
}) {
  return (
    <Button variant="ghost" onClick={onClick} disabled={loading} style={{
      padding: '10px 18px', borderRadius: 10, border: `1px solid ${_T.borderSolid}`,
      background: active ? T.successBg : T.bg,
      color: active ? T.success : T.text,
      fontSize: 13, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
      fontFamily: _T.font, display: 'flex', alignItems: 'center', gap: 6,
      transition: 'all 0.2s', opacity: loading ? 0.6 : 1,
    }}>
 {loading ? '' : icon} {active ? activeLabel : label}
    </Button>
  );
}

function PageBtn({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <Button variant="ghost" onClick={onClick} disabled={disabled} style={{
      padding: '8px 16px', borderRadius: 8, border: `1px solid ${_T.borderSolid}`,
      background: disabled ? 'transparent' : T.bg,
      color: disabled ? _T.dimmed : T.text,
      fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
      fontFamily: _T.font, opacity: disabled ? 0.4 : 1,
    }}>
      {label}
    </Button>
  );
}

function SettingsSection({ title, children }: { title: string; children: any }) {
  return (
    <div>
      <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: T.text }}>{title}</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

function SettingsBtn({ label, icon, desc, onClick, loading, color }: {
  label: string; icon: string; desc: string; onClick: () => void; loading: boolean; color: string;
}) {
  return (
    <div style={{
      padding: 16, borderRadius: 12, border: `1px solid ${_T.borderSolid}`,
      background: T.bg, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon} {label}
        </div>
        <div style={{ fontSize: 12, color: _T.muted, marginTop: 4 }}>{desc}</div>
      </div>
      <Button variant="ghost" onClick={onClick} disabled={loading} style={{
        padding: '8px 18px', borderRadius: 8, border: 'none',
        background: `${color}18`, color,
        fontSize: 12, fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
        fontFamily: _T.font, opacity: loading ? 0.6 : 1,
      }}>
 {loading ? '...' : 'Executar'}
      </Button>
    </div>
  );
}
